import React from 'react';
import _ from 'lodash';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

interface IBaseRemoAdminLinkProps {
  className?: string;
  openInCurrentTab?: boolean;
}

interface IRemoAdminLinkProps extends IBaseRemoAdminLinkProps {
  displayValue: React.ReactNode;
  href: string;
  queryParams?: Record<string, string>;
  onClick?: () => void;
}

export const RemoAdminLink: React.FC<IRemoAdminLinkProps> = props => {
  const { href, className, queryParams, openInCurrentTab, onClick } = props;
  const baseHref = window.location.href.includes('/expert')
    ? href.replace('remoadmin', 'expert/outlieradmin')
    : href;
  const url = new URL(window.location.href);
  url.pathname = baseHref;

  if (queryParams) {
    _.forEach(queryParams, (value, key) => url.searchParams.set(key, value));
  }

  const target = openInCurrentTab ? '_self' : '_blank';
  return (
    <a
      className={className}
      href={url.toString()}
      target={target}
      onClick={onClick}
      rel="noopener noreferrer"
    >
      {props.displayValue}
    </a>
  );
};

interface IRemoAdminLookupLinkProps extends IBaseRemoAdminLinkProps {
  id: string;
  displayValue?: string;
}

// RemoAdminLookupLink provides an `<a>` anchor element redirecting to the lookup tool.
// This link preserves any query params and opens the item lookup in a new tab.
export const RemoAdminLookupLink: React.FC<IRemoAdminLookupLinkProps> = ({
  id,
  className,
  openInCurrentTab,
  ...rest
}) => {
  const displayValue = rest.displayValue ?? id;
  const linkProps = { className, openInCurrentTab, displayValue };
  const { isRemoAdmin } = useRemoAdminCtx();
  const hrefPrefix = React.useMemo<string>(
    () => (isRemoAdmin ? '/remoadmin/tools' : '/corp'),
    [isRemoAdmin],
  );
  return <RemoAdminLink href={`${hrefPrefix}/lookup/${id}`} {...linkProps} />;
};

export const RemoAdminProjectLink: React.FC<IRemoAdminLookupLinkProps> = ({
  id,
  className,
  openInCurrentTab,
  ...rest
}) => {
  const { currentPersona } = useRemoAdminCtx();
  const projects: string[] = currentPersona?.projects ?? [];
  const hasAccess = currentPersona?.applyToAllProjects || projects.indexOf(id) !== -1;

  if (!hasAccess) return <></>;

  const displayValue = rest.displayValue ?? id;
  const queryParams = { projectId: id };
  const linkProps = { className, openInCurrentTab, displayValue, queryParams };

  return <RemoAdminLink href={`/remoadmin`} {...linkProps} />;
};
